<!-- Header/Top Bar Component -->
<script setup lang="ts">
import { computed } from 'vue'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { useTheme } from '@/composables/useTheme'
import Button from '@/components/ui/Button.vue'

const uiStore = useUiStore()
const authStore = useAuthStore()
const { theme, toggleTheme, isDark } = useTheme()

const props = defineProps<{
  bannerHeight?: number
}>()

const pageTitle = computed(() => uiStore.pageTitle)

const headerStyles = computed(() => ({
  top: `${props.bannerHeight || 0}px`,
}))

function handleMobileMenuClick() {
  uiStore.toggleSidebar()
}
</script>

<template>
  <header class="header" role="banner" :style="headerStyles">
    <div class="header__left">
      <button
        type="button"
        class="header__menu-btn"
        @click="handleMobileMenuClick"
        aria-label="Abrir menú"
        :aria-expanded="uiStore.sidebarOpen"
        aria-controls="sidebar"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      
      <h1 class="header__title">{{ pageTitle }}</h1>
    </div>
    
    <div class="header__right">
      <!-- Theme Toggle -->
      <button
        type="button"
        class="header__theme-btn"
        @click="toggleTheme"
        :aria-label="isDark() ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
        :aria-pressed="isDark()"
        title="isDark() ? 'Modo oscuro' : 'Modo claro'"
      >
        <svg v-if="isDark()" class="header__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
        <svg v-else class="header__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      </button>
      
      <!-- User Menu -->
      <div class="header__user" v-if="authStore.isAuthenticated">
        <div class="header__user-avatar" @click="uiStore.toggleModal('user-menu')">
          <span class="header__user-initial">{{ authStore.user?.username?.charAt(0).toUpperCase() || 'U' }}</span>
        </div>
      </div>
    </div>
    
    <!-- User Dropdown Modal -->
    <Teleport to="body">
      <div
        v-if="uiStore.modals['user-menu']"
        class="dropdown-backdrop"
        @click="uiStore.closeModal('user-menu')"
        aria-hidden="true"
      />
    </Teleport>
    
    <div
      v-if="uiStore.modals['user-menu']"
      class="dropdown-menu"
      role="menu"
      aria-orientation="vertical"
    >
      <div class="dropdown__header">
        <div class="dropdown__avatar">{{ authStore.user?.username?.charAt(0).toUpperCase() || 'U' }}</div>
        <div>
          <p class="dropdown__name">{{ authStore.user?.username }}</p>
          <p class="dropdown__email">{{ authStore.user?.email }}</p>
        </div>
      </div>
      <hr class="dropdown__divider" />
      <button
        type="button"
        class="dropdown__item"
        role="menuitem"
        @click="uiStore.closeModal('user-menu')"
      >
        <svg class="dropdown__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        Mi perfil
      </button>
      <button
        type="button"
        class="dropdown__item"
        role="menuitem"
        @click="uiStore.closeModal('user-menu')"
      >
        <svg class="dropdown__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
        Configuración
      </button>
      <hr class="dropdown__divider" />
      <button
        type="button"
        class="dropdown__item dropdown__item--danger"
        role="menuitem"
        @click="authStore.logout(); uiStore.closeModal('user-menu')"
      >
        <svg class="dropdown__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
        Cerrar sesión
      </button>
    </div>
  </header>
</template>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--header-height);
  background: var(--color-bg-card);
  border-bottom: 1px solid var(--color-border-subtle);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-lg);
  z-index: var(--z-sticky);
  flex-shrink: 0;
}

.header__left {
  display: flex;
  align-items: center;
  gap: var(--space-md);
}

.header__menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.header__menu-btn:hover {
  background: var(--color-bg-hover);
  color: var(--color-text-primary);
}

.header__menu-btn:focus-visible {
  outline: 2px solid var(--color-brand-primary);
  outline-offset: 2px;
}

.header__menu-btn svg {
  width: 24px;
  height: 24px;
}

@media (min-width: 1024px) {
  .header__menu-btn {
    display: none;
  }
}

.header__title {
  font-size: var(--text-lg);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.header__right {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.header__theme-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.header__theme-btn:hover {
  background: var(--color-bg-hover);
  color: var(--color-text-primary);
}

.header__theme-btn:focus-visible {
  outline: 2px solid var(--color-brand-primary);
  outline-offset: 2px;
}

.header__icon {
  width: 22px;
  height: 22px;
}

.header__user {
  position: relative;
}

.header__user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--color-brand-primary-light);
  border-radius: var(--radius-full);
  color: var(--color-brand-primary);
  font-weight: var(--font-semibold);
  font-size: var(--text-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
  user-select: none;
}

.header__user-avatar:hover {
  background: var(--color-brand-primary);
  color: var(--color-text-inverse);
}

/* Dropdown */
.dropdown-backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal-backdrop);
}

.dropdown-menu {
  position: fixed;
  top: calc(var(--header-height) + var(--space-sm));
  right: var(--space-lg);
  min-width: 220px;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  z-index: var(--z-popover);
  overflow: hidden;
  animation: dropdownIn var(--transition-fast);
}

@keyframes dropdownIn {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

.dropdown__header {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-md);
}

.dropdown__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--color-brand-primary-light);
  border-radius: var(--radius-full);
  color: var(--color-brand-primary);
  font-weight: var(--font-bold);
  font-size: var(--text-base);
  flex-shrink: 0;
}

.dropdown__name {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-text-primary);
  margin: 0;
}

.dropdown__email {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  margin: 0;
}

.dropdown__divider {
  border: none;
  border-top: 1px solid var(--color-border-subtle);
  margin: 0;
}

.dropdown__item {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  width: 100%;
  padding: var(--space-sm) var(--space-md);
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
  text-align: left;
}

.dropdown__item:hover {
  background: var(--color-bg-hover);
  color: var(--color-text-primary);
}

.dropdown__item:focus-visible {
  outline: 2px solid var(--color-brand-primary);
  outline-offset: -2px;
}

.dropdown__item--danger {
  color: var(--color-error);
}

.dropdown__item--danger:hover {
  background: var(--color-error-light);
  color: var(--color-error);
}

.dropdown__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: currentColor;
}

/* Mobile adjustments */
@media (max-width: 480px) {
  .header {
    padding: 0 var(--space-md);
  }
  
  .header__title {
    max-width: 180px;
    font-size: var(--text-base);
  }
  
  .dropdown-menu {
    right: var(--space-md);
    left: var(--space-md);
    min-width: auto;
  }
}
</style>